/**
 * Zufriedenheit — Customer satisfaction surveys page.
 * KPI cards, trend chart, response rate chart, survey list, detail view,
 * settings and question management.
 *
 * @page /zufriedenheit
 * @roles admin, manager
 * @i18n survey, common
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ToggleSwitch } from '../components/ui';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  Plus, Pencil, Trash2, Save, Star, Send, Download, ArrowLeft,
  MoreHorizontal, X, Bell, Heart, Settings, Eye, FlaskConical,
} from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../context/AuthContext';

const API = import.meta.env.VITE_API_URL || '';
const inp = "w-full px-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500";

const CHART_COLORS = ['#338edc', '#22D3EE', '#6366F1', '#F59E0B', '#10B981', '#EF4444'];
const GESAMT_COLOR = '#9CA3AF';

const SURVEY_STATUS_BADGE = {
  draft:     'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  sent:      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  closed:    'bg-gray-100 text-[var(--text-primary,#1A1A2E)] dark:bg-gray-800 dark:text-[var(--text-tertiary,#6B7280)]',
};

const TYP_BADGES = {
  stars: { label: '\u2605 Stars', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  nps:   { label: '\ud83d\udcca NPS',  cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  text:  { label: '\ud83d\udcdd Text', cls: 'bg-gray-100 text-[var(--text-primary,#1A1A2E)] dark:bg-gray-800 dark:text-[var(--text-tertiary,#6B7280)]' },
  yesno: { label: '\u2713 Ja/Nein', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
};

const LEGACY_MAP = { 1: 'versand', 2: 'support', 3: 'inbound' };
const LEGACY_COL = { 1: 'bewertung_versand', 2: 'bewertung_support', 3: 'bewertung_inbound' };

const Spinner = () => (
  <div className="flex items-center justify-center h-48">
    <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
  </div>
);

/* ── Star display (read-only) ── */
function StarDisplay({ value }) {
  if (value == null) return <span className="text-xs text-[var(--text-tertiary)]">&mdash;</span>;
  const v = Number(value);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} className={`text-sm ${i <= Math.round(v) ? 'text-yellow-400' : 'text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)]'}`}>&#9733;</span>
      ))}
      <span className="text-xs text-[var(--text-secondary)] ml-1">{v.toFixed(1)}</span>
    </div>
  );
}

/* ── Toast ── */
function Toast({ message, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-card bg-surface-elevated border border-line shadow-sm text-sm text-content animate-in fade-in slide-in-from-bottom-4">
      {message}
    </div>
  );
}

/* ── Confirm Modal ── */
function ConfirmModal({ open, message, onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-surface-elevated border border-line rounded-card shadow-elevated w-full max-w-sm p-6">
        <p className="text-sm text-content mb-5">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">
            Abbrechen
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
            {loading ? '...' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── New Survey Modal ── */
function NewSurveyModal({ open, onClose, onCreated, t }) {
  const [monat, setMonat] = useState(() => {
    const now = new Date();
    return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/surveys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ monat }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(res.status === 409 ? t('survey.admin.existsError') : (data.error || 'Error'));
        return;
      }
      onCreated();
      onClose();
    } catch { setError('Error'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-elevated border border-line rounded-card shadow-elevated w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-content">{t('survey.admin.create')}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)]"><X className="w-4 h-4" /></button>
        </div>
        <label className="block mb-4">
          <span className="text-xs font-medium text-[var(--text-secondary)]">{t('survey.admin.selectMonth')}</span>
          <input type="month" value={monat} onChange={e => setMonat(e.target.value)} className={`mt-1 ${inp}`} />
        </label>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">{t('common.cancel')}</button>
          <button onClick={handleCreate} disabled={saving || !monat}
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
            {saving ? '...' : t('survey.admin.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Custom Recharts Tooltip ── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-elevated border border-line rounded-lg shadow-sm px-3 py-2 text-xs">
      <p className="font-semibold text-content mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
          <span className="text-[var(--text-secondary)]">{p.name}:</span>
          <span className="font-semibold text-content tabular-nums">{p.value != null ? p.value.toFixed(1) : '\u2014'}</span>
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════════ */
export default function Zufriedenheit() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => setToast(msg), []);
  const [confirmDeleteFrage, setConfirmDeleteFrage] = useState(null);

  // Data
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [surveyFragen, setSurveyFragen] = useState([]);

  // Settings
  const [settings, setSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Fragen
  const [fragen, setFragen] = useState([]);
  const [fragenLoading, setFragenLoading] = useState(true);
  const [editFrage, setEditFrage] = useState(null);
  const [frageSaving, setFrageSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  // Detail
  const [detailSurvey, setDetailSurvey] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // UI state
  const [showNewModal, setShowNewModal] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [period, setPeriod] = useState('12m');
  const [showSettings, setShowSettings] = useState(false);
  const [empfaengerModal, setEmpfaengerModal] = useState(null); // { surveyId, empfaenger[], loading, sending, search }


  // ── Load stats ──
  const loadStats = useCallback(async () => {
    try {
      const d = await fetch(`${API}/api/surveys/stats`, { credentials: 'include' }).then(r => r.json());
      if (d.ok) setStats(d);
    } catch { /* ignore */ }
    setStatsLoading(false);
  }, []);

  // ── Load surveys ──
  const loadSurveys = useCallback(async () => {
    try {
      const d = await fetch(`${API}/api/surveys`, { credentials: 'include' }).then(r => r.json());
      setSurveys(d.surveys || []);
      setSurveyFragen(d.fragen || []);
    } catch { /* ignore */ }
    setSurveysLoading(false);
  }, []);

  // ── Load settings ──
  const loadSettings = useCallback(async () => {
    try {
      const d = await fetch(`${API}/api/surveys/settings`, { credentials: 'include' }).then(r => r.json());
      setSettings(d.settings || {});
    } catch { /* ignore */ }
    setSettingsLoading(false);
  }, []);

  // ── Load fragen ──
  const loadFragen = useCallback(async () => {
    try {
      const d = await fetch(`${API}/api/surveys/fragen`, { credentials: 'include' }).then(r => r.json());
      setFragen(d.fragen || []);
    } catch { /* ignore */ }
    setFragenLoading(false);
  }, []);

  useEffect(() => { loadStats(); loadSurveys(); loadSettings(); loadFragen(); }, [loadStats, loadSurveys, loadSettings, loadFragen]);

  // ── Save settings ──
  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await fetch(`${API}/api/surveys/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ settings }),
      });
      showToast(t('survey.admin.settingsSaved'));
    } catch { showToast('Error'); }
    setSettingsSaving(false);
  };

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
  const isTrue = (val) => val === 'true' || val === true;

  // ── Survey actions ──
  const surveyAction = async (id, action, successKey) => {
    setConfirm(prev => ({ ...prev, loading: true }));
    try {
      const method = action === 'delete' ? 'DELETE' : 'POST';
      const url = action === 'delete' ? `${API}/api/surveys/${id}` : `${API}/api/surveys/${id}/${action}`;
      const res = await fetch(url, { method, credentials: 'include' });
      if (res.ok) {
        showToast(t(successKey));
        loadSurveys();
        loadStats();
      } else {
        const d = await res.json().catch(() => ({}));
        showToast(d.error || 'Error');
      }
    } catch { showToast('Error'); }
    setConfirm(null);
  };

  const confirmAction = (message, action) => {
    setConfirm({ message, action, loading: false });
  };

  // ── Export ──
  const handleExport = async (surveyId, monat) => {
    try {
      const res = await fetch(`${API}/api/surveys/${surveyId}/export`, { credentials: 'include' });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survey-${monat}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { showToast('Error'); }
  };

  // ── Test send ──
  const handleTestSend = async (surveyId) => {
    setConfirm(prev => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`${API}/api/surveys/${surveyId}/test-send`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await res.json();
      if (res.ok) {
        showToast(t('survey.actions.testSent'));
      } else {
        showToast(d.error || 'Error');
      }
    } catch { showToast('Error'); }
    setConfirm(null);
  };

  // ── Empfänger-Modal ──
  const openEmpfaengerModal = async (surveyId) => {
    setEmpfaengerModal({ surveyId, empfaenger: [], loading: true, sending: false, search: '' });
    try {
      const res = await fetch(`${API}/api/surveys/${surveyId}/empfaenger`, { credentials: 'include' });
      const d = await res.json();
      if (d.ok) {
        setEmpfaengerModal(prev => ({ ...prev, empfaenger: d.empfaenger, loading: false }));
      } else {
        showToast(d.error || 'Error');
        setEmpfaengerModal(null);
      }
    } catch { showToast('Error'); setEmpfaengerModal(null); }
  };

  const toggleEmpfaenger = (kundeId) => {
    setEmpfaengerModal(prev => ({
      ...prev,
      empfaenger: prev.empfaenger.map(e =>
        e.kunde_id === kundeId && e.email ? { ...e, selected: !e.selected } : e
      ),
    }));
  };

  const toggleAllEmpfaenger = (checked) => {
    setEmpfaengerModal(prev => ({
      ...prev,
      empfaenger: prev.empfaenger.map(e => e.email ? { ...e, selected: checked } : e),
    }));
  };

  const sendToSelected = async () => {
    if (!empfaengerModal) return;
    const selected = empfaengerModal.empfaenger.filter(e => e.selected);
    if (selected.length === 0) return;

    setEmpfaengerModal(prev => ({ ...prev, sending: true }));
    try {
      const res = await fetch(`${API}/api/surveys/${empfaengerModal.surveyId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ kunde_ids: selected.map(e => e.kunde_id) }),
      });
      if (res.ok) {
        showToast(t('survey.admin.sendSuccess'));
        loadSurveys();
        loadStats();
        setEmpfaengerModal(null);
      } else {
        const d = await res.json().catch(() => ({}));
        showToast(d.error || 'Error');
        setEmpfaengerModal(prev => ({ ...prev, sending: false }));
      }
    } catch {
      showToast('Error');
      setEmpfaengerModal(prev => ({ ...prev, sending: false }));
    }
  };

  // ── Load detail ──
  const loadDetail = async (id) => {
    setDetailLoading(true);
    try {
      const d = await fetch(`${API}/api/surveys/${id}`, { credentials: 'include' }).then(r => r.json());
      setDetailSurvey(d);
    } catch { showToast('Error'); }
    setDetailLoading(false);
  };

  // ── Fragen actions ──
  const saveFrage = async (data) => {
    setFrageSaving(true);
    try {
      const isNew = !data.id;
      const url = isNew ? `${API}/api/surveys/fragen` : `${API}/api/surveys/fragen/${data.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (res.ok) {
        showToast(isNew ? t('survey.fragen.created') : t('survey.fragen.saved'));
        loadFragen();
        setEditFrage(null);
      } else {
        const d = await res.json().catch(() => ({}));
        showToast(d.error || 'Error');
      }
    } catch { showToast('Error'); }
    setFrageSaving(false);
  };

  const deleteFrage = async (id) => {
    try {
      const res = await fetch(`${API}/api/surveys/fragen/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        showToast(t('survey.fragen.deleted'));
        loadFragen();
      }
    } catch { showToast('Error'); }
  };

  const reorderFragen = async (fromIdx, toIdx) => {
    const items = [...fragen.filter(f => f.aktiv)];
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    setFragen([...items, ...fragen.filter(f => !f.aktiv)]);
    try {
      await fetch(`${API}/api/surveys/fragen/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: items.map(f => f.id) }),
      });
    } catch { loadFragen(); }
  };

  // ── Format helpers ──
  const formatMonat = (m) => {
    if (!m) return '\u2014';
    const [y, mo] = m.split('-');
    return `${mo}/${y.slice(2)}`;
  };
  const formatDate = (d) => {
    if (!d) return '\u2014';
    return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };
  const formatMonatLabel = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${names[parseInt(mo) - 1]} ${y.slice(2)}`;
  };

  // ── Chart data ──
  const chartData = useMemo(() => {
    if (!stats?.trend) return [];
    let data = stats.trend;
    if (period === '6m') data = data.slice(-6);
    else if (period === '12m') data = data.slice(-12);
    return data.map(t => {
      const point = { monat: formatMonatLabel(t.monat), gesamt: t.gesamt_avg };
      for (const f of t.fragen) {
        point[`f_${f.frage_id}`] = f.avg;
      }
      return point;
    });
  }, [stats, period]);

  const responseChartData = useMemo(() => {
    if (!stats?.trend) return [];
    let data = stats.trend;
    if (period === '6m') data = data.slice(-6);
    else if (period === '12m') data = data.slice(-12);
    return data.map(t => ({
      monat: formatMonatLabel(t.monat),
      empfaenger: t.empfaenger || 0,
      antworten: t.antworten || 0,
    }));
  }, [stats, period]);

  const starsFragen = useMemo(() => {
    return (stats?.fragen || []).filter(f => f.typ === 'stars');
  }, [stats]);

  const aktiveFragen = fragen.filter(f => f.aktiv);

  // ══════════════════════════════════════
  //  DETAIL VIEW
  // ══════════════════════════════════════
  if (detailSurvey) {
    const { survey, einladungen = [], stats: detailStats, fragen: detailFragen = [], fragenStats = {} } = detailSurvey;
    const beantwortet = einladungen.filter(e => e.antworten_json || e.bewertung_versand != null);
    const nichtBeantwortet = einladungen.filter(e => !e.antworten_json && e.bewertung_versand == null);
    const ruecklauf = survey?.empfaenger_count > 0
      ? ((survey.antworten_count / survey.empfaenger_count) * 100).toFixed(1)
      : '0.0';
    const ratingFragen = detailFragen.filter(f => f.typ !== 'text');

    if (detailLoading) return <div className="p-8"><Spinner /></div>;

    return (
      <div className="p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setDetailSurvey(null)} className="p-2 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] hover:text-content transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h2 className="text-base font-bold text-content">{survey?.titel || `Umfrage ${formatMonat(survey?.monat)}`}</h2>
              <p className="text-xs text-[var(--text-secondary)]">{survey?.antworten_count || 0} {t('survey.admin.antworten')} ({ruecklauf}% {t('survey.admin.ruecklaufquote')})</p>
            </div>
          </div>
          <button onClick={() => handleExport(survey?.id, survey?.monat)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-line text-content hover:bg-surface-hover transition-colors">
            <Download className="w-3.5 h-3.5" /> {t('survey.admin.export')}
          </button>
        </div>

        {/* Per-question averages */}
        <div className="bg-surface-elevated rounded-card border border-line">
          <div className="px-5 py-3 border-b border-line">
            <h3 className="text-sm font-semibold text-content">{t('survey.admin.durchschnitt')}</h3>
          </div>
          <div className="px-5 py-4 space-y-3">
            {ratingFragen.map(f => (
              <div key={f.id} className="flex items-center justify-between">
                <span className="text-sm text-content">{f.label_de}</span>
                {f.typ === 'stars' ? (
                  <StarDisplay value={fragenStats[f.id]?.avg ?? detailStats?.[`avg_${LEGACY_MAP[f.id]}`]} />
                ) : (
                  <span className="text-sm font-semibold text-content tabular-nums">
                    {fragenStats[f.id]?.avg != null ? fragenStats[f.id].avg.toFixed(1) : '\u2014'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Answers table */}
        {beantwortet.length > 0 && (
          <div className="bg-surface-elevated rounded-card border border-line overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('survey.admin.kunde')}</th>
                  {detailFragen.map(f => (
                    <th key={f.id} className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{f.label_de}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line/50">
                {beantwortet.map(e => {
                  const arr = e.antworten_json || [];
                  return (
                    <tr key={e.id} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
                      <td className="px-5 py-2.5 text-content font-medium">{e.kunde_name}</td>
                      {detailFragen.map(f => {
                        const a = arr.find(x => x.frage_id === f.id);
                        let cell;
                        if (f.typ === 'stars') {
                          cell = <StarDisplay value={a?.wert ?? (f.id <= 3 ? e[LEGACY_COL[f.id]] : null)} />;
                        } else if (f.typ === 'text') {
                          const txt = a?.text || (f.id === 4 ? e.feedback_text : null);
                          cell = txt
                            ? <span className="text-xs text-[var(--text-secondary)] max-w-xs truncate block">{txt}</span>
                            : <span className="text-[var(--text-tertiary)] text-xs">&mdash;</span>;
                        } else if (f.typ === 'nps') {
                          cell = a?.wert != null ? <span className="text-sm font-semibold tabular-nums">{a.wert}</span> : <span className="text-xs text-[var(--text-tertiary)]">&mdash;</span>;
                        } else if (f.typ === 'yesno') {
                          cell = a?.wert != null ? <span className="text-xs">{a.wert === 1 ? '\u2713' : '\u2717'}</span> : <span className="text-xs text-[var(--text-tertiary)]">&mdash;</span>;
                        } else {
                          cell = '\u2014';
                        }
                        return <td key={f.id} className="px-3 py-2.5">{cell}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {nichtBeantwortet.length > 0 && (
          <div className="bg-surface-elevated rounded-card border border-line">
            <div className="px-5 py-3 border-b border-line">
              <h3 className="text-sm font-semibold text-content">{t('survey.admin.nichtBeantwortet')} ({nichtBeantwortet.length})</h3>
            </div>
            <div className="px-5 py-3">
              <p className="text-xs text-[var(--text-secondary)]">
                {nichtBeantwortet.map(e => e.kunde_name).join(', ')}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════
  //  MAIN VIEW
  // ══════════════════════════════════════
  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-content">{t('survey.page.title')}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">{t('survey.page.subtitle')}</p>
        </div>
      </div>

      {/* ── 5 KPI Cards ── */}
      {statsLoading ? <Spinner /> : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            {
              label: t('survey.kpi.avgScore'),
              value: stats?.gesamt_score != null ? `${stats.gesamt_score.toFixed(1)} / 5` : '\u2014',
              icon: Star, color: '#F59E0B',
            },
            {
              label: t('survey.kpi.responseRate'),
              value: stats?.ruecklauf_quote != null ? `${Math.round(stats.ruecklauf_quote * 100)}%` : '\u2014',
              icon: Heart, color: '#F59E0B',
            },
            {
              label: t('survey.kpi.totalResponses'),
              value: stats?.total_antworten ?? '\u2014',
              icon: Send, color: '#F59E0B',
            },
            {
              label: t('survey.kpi.lastSurvey'),
              value: stats?.letzte_umfrage ? formatMonatLabel(stats.letzte_umfrage) : '\u2014',
              icon: Bell, color: '#F59E0B',
            },
            {
              label: t('survey.nps.score'),
              value: stats?.nps_score != null ? stats.nps_score : '\u2014',
              icon: FlaskConical, color: '#F59E0B',
              badge: stats?.nps_score != null ? (
                stats.nps_score > 50 ? { text: t('survey.nps.excellent'), cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
                : stats.nps_score >= 0 ? { text: t('survey.nps.good'), cls: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' }
                : stats.nps_score >= -50 ? { text: t('survey.nps.needsWork'), cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' }
                : { text: t('survey.nps.critical'), cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
              ) : null,
            },
          ].map((kpi, i) => {
            const Icon = kpi.icon;
            return (
            <div key={i} className="bg-surface-elevated rounded-card border border-line p-5">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className="text-[#F59E0B]" />
                <p className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{kpi.label}</p>
              </div>
              <p className="text-[28px] font-bold text-[var(--text-primary)] mt-1 tabular-nums">{kpi.value}</p>
              {kpi.badge && (
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${kpi.badge.cls}`}>{kpi.badge.text}</span>
              )}
            </div>
          ); })}
        </div>
      )}

      {/* ── Trend-Chart ── */}
      {stats?.trend?.length > 0 && (
        <div className="bg-surface-elevated rounded-card border border-line">
          <div className="px-5 py-3 border-b border-line flex items-center justify-between">
            <h2 className="text-sm font-semibold text-content">{t('survey.chart.title')}</h2>
            {/* Period Toggle */}
            <div className="flex items-center bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line">
              {[
                { key: '6m', label: t('survey.period.6m') },
                { key: '12m', label: t('survey.period.12m') },
                { key: 'all', label: t('survey.period.all') },
              ].map(p => (
                <button key={p.key} onClick={() => setPeriod(p.key)}
                  className={`px-3 py-1 text-xs font-medium rounded-[8px] transition-all ${
                    period === p.key
                      ? 'bg-white dark:bg-[var(--bg-elevated)] text-content shadow-sm'
                      : 'text-[var(--text-secondary)] hover:text-content'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="px-5 py-4" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                <XAxis dataKey="monat" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                <YAxis domain={[1, 5]} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                <RechartsTooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {starsFragen.map((f, i) => (
                  <Line
                    key={f.id}
                    type="monotone"
                    dataKey={`f_${f.id}`}
                    name={f.label_de}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="gesamt"
                  name={t('survey.chart.average')}
                  stroke={GESAMT_COLOR}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 3 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Response Rate Chart ── */}
      {responseChartData.length > 0 && (
        <div className="bg-surface-elevated rounded-card border border-line">
          <div className="px-5 py-3 border-b border-line">
            <h2 className="text-sm font-semibold text-content">{t('survey.chart.responseRate')}</h2>
          </div>
          <div className="px-5 py-4" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={responseChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #E5E7EB)" />
                <XAxis dataKey="monat" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                <RechartsTooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="empfaenger" name={t('survey.admin.versendet')} fill="#E5E7EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="antworten" name={t('survey.admin.antworten')} fill="#338edc" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Survey List ── */}
      <div className="bg-surface-elevated rounded-card border border-line">
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <h2 className="text-sm font-semibold text-content">{t('survey.list.title')}</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSettings(!showSettings)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-line text-content hover:bg-surface-hover transition-colors">
              <Settings className="w-3.5 h-3.5" /> {t('survey.list.settings')}
            </button>
            <button onClick={() => setShowNewModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
              <Plus className="w-3.5 h-3.5" /> {t('survey.list.newSurvey')}
            </button>
          </div>
        </div>

        {surveysLoading ? <Spinner /> : surveys.length === 0 ? (
          <div className="py-12 text-center">
            <Heart className="w-10 h-10 text-[var(--text-tertiary)] mx-auto mb-3" />
            <p className="text-sm text-[var(--text-tertiary)]">{t('survey.admin.noSurveys')}</p>
            <button onClick={() => setShowNewModal(true)}
              className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
              {t('survey.list.newSurvey')}
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="text-left px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('survey.admin.monat')}</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">Status</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('survey.admin.versendet')}</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('survey.admin.antworten')}</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('survey.admin.ruecklaufquote')}</th>
                <th className="text-right px-5 py-2.5 text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('survey.admin.aktion')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/50">
              {surveys.map(s => {
                const ruecklauf = s.empfaenger_count > 0
                  ? ((s.antworten_count / s.empfaenger_count) * 100).toFixed(1)
                  : null;
                return (
                  <tr key={s.id} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors" style={{ height: 52 }}>
                    <td className="px-5 py-2.5 text-content font-medium">{formatMonat(s.monat)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${SURVEY_STATUS_BADGE[s.status] || SURVEY_STATUS_BADGE.draft}`}>
                        {t(`survey.admin.status.${s.status}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-content-secondary text-xs">{formatDate(s.gesendet_at)}</td>
                    <td className="px-3 py-2.5 text-content tabular-nums">
                      {s.status === 'draft' ? '\u2014' : `${s.antworten_count || 0}/${s.empfaenger_count || 0}`}
                    </td>
                    <td className="px-3 py-2.5 text-content tabular-nums">{ruecklauf != null ? `${ruecklauf}%` : '\u2014'}</td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {/* Preview + Test — always available */}
                        <button onClick={() => window.open('/feedback/preview', '_blank')}
                          className="px-2 py-1 rounded-lg text-[10px] font-semibold border border-line text-content hover:bg-surface-hover transition-colors"
                          title={t('survey.actions.preview')}><Eye className="w-3 h-3" /></button>
                        <button onClick={() => confirmAction(t('survey.actions.testSendConfirm', { email: user?.email || '' }), () => handleTestSend(s.id))}
                          className="px-2 py-1 rounded-lg text-[10px] font-semibold border border-line text-content hover:bg-surface-hover transition-colors"
                          title={t('survey.actions.testSend')}><FlaskConical className="w-3 h-3" /></button>
                        {s.status === 'draft' && (
                          <>
                            <button onClick={() => openEmpfaengerModal(s.id)}
                              className="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors"
                              title={t('survey.actions.send')}><Send className="w-3 h-3" /></button>
                            <button onClick={() => confirmAction(t('survey.admin.confirmDelete'), () => surveyAction(s.id, 'delete', 'survey.admin.deleteSuccess'))}
                              className="p-1 rounded-lg hover:bg-surface-hover text-red-400 hover:text-red-300 transition-colors"
                              title={t('survey.admin.delete')}><Trash2 className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                        {s.status === 'sent' && (
                          <>
                            <button onClick={() => confirmAction(t('survey.admin.confirmRemind'), () => surveyAction(s.id, 'remind', 'survey.admin.remindSuccess'))}
                              className="px-2 py-1 rounded-lg text-[10px] font-semibold border border-line text-content hover:bg-surface-hover transition-colors"
                              title={t('survey.actions.remind')}><Bell className="w-3 h-3" /></button>
                            <button onClick={() => confirmAction(t('survey.admin.confirmClose'), () => surveyAction(s.id, 'close', 'survey.admin.closeSuccess'))}
                              className="px-2 py-1 rounded-lg text-[10px] font-semibold border border-line text-content hover:bg-surface-hover transition-colors"
                              title={t('survey.actions.close')}><X className="w-3 h-3" /></button>
                            <button onClick={() => handleExport(s.id, s.monat)}
                              className="px-2 py-1 rounded-lg text-[10px] font-semibold border border-line text-content hover:bg-surface-hover transition-colors"
                              title={t('survey.actions.export')}><Download className="w-3 h-3" /></button>
                            <button onClick={() => loadDetail(s.id)}
                              className="px-2 py-1 rounded-lg text-[10px] font-semibold border border-line text-content hover:bg-surface-hover transition-colors"
                              title={t('survey.actions.detail')}><MoreHorizontal className="w-3 h-3" /></button>
                          </>
                        )}
                        {s.status === 'closed' && (
                          <>
                            <button onClick={() => handleExport(s.id, s.monat)}
                              className="px-2 py-1 rounded-lg text-[10px] font-semibold border border-line text-content hover:bg-surface-hover transition-colors"
                              title={t('survey.actions.export')}><Download className="w-3 h-3" /></button>
                            <button onClick={() => loadDetail(s.id)}
                              className="px-2 py-1 rounded-lg text-[10px] font-semibold border border-line text-content hover:bg-surface-hover transition-colors"
                              title={t('survey.actions.detail')}><MoreHorizontal className="w-3 h-3" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Settings + Fragen (collapsible) ── */}
      {showSettings && (
        <>
          {/* Fragen verwalten */}
          <div className="bg-surface-elevated rounded-card border border-line">
            <div className="px-5 py-3 border-b border-line flex items-center justify-between">
              <h2 className="text-sm font-semibold text-content">{t('survey.fragen.title')}</h2>
              <button onClick={() => setEditFrage({ label_de: '', label_en: '', beschreibung_de: '', beschreibung_en: '', typ: 'stars', pflicht: true })}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
                <Plus className="w-3.5 h-3.5" /> {t('survey.fragen.add')}
              </button>
            </div>
            {fragenLoading ? <Spinner /> : aktiveFragen.length === 0 ? (
              <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('survey.fragen.empty')}</div>
            ) : (
              <div className="divide-y divide-line/50">
                {aktiveFragen.map((f, idx) => (
                  <div
                    key={f.id}
                    draggable
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => { if (dragIdx != null && dragIdx !== idx) reorderFragen(dragIdx, idx); setDragIdx(null); }}
                    onDragEnd={() => setDragIdx(null)}
                    className={`flex items-center gap-3 px-5 py-3 hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors ${dragIdx === idx ? 'opacity-50' : ''}`}
                  >
                    <span className="cursor-grab text-[var(--text-tertiary)] text-sm select-none">{'\u2807'}</span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-content font-medium">{f.label_de}</span>
                      {f.label_en && <span className="text-xs text-[var(--text-tertiary)] ml-2">({f.label_en})</span>}
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded whitespace-nowrap ${TYP_BADGES[f.typ]?.cls || ''}`}>
                      {TYP_BADGES[f.typ]?.label || f.typ}
                    </span>
                    {f.pflicht && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                        {t('survey.fragen.required')}
                      </span>
                    )}
                    <button onClick={() => setEditFrage({ ...f })} className="p-1 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)] transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setConfirmDeleteFrage(f.id)}
                      className="p-1 rounded-lg hover:bg-surface-hover text-red-400 hover:text-red-300 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Einstellungen */}
          <div className="bg-surface-elevated rounded-card border border-line">
            <div className="px-5 py-3 border-b border-line">
              <h2 className="text-sm font-semibold text-content flex items-center gap-1.5">
                <Star className="w-4 h-4" /> {t('survey.admin.settings')}
              </h2>
            </div>
            {settingsLoading ? <Spinner /> : (
              <div className="px-5 divide-y divide-line">
                <div className="flex items-center justify-between gap-6 py-4">
                  <div className="text-sm font-medium text-content">{t('survey.config.enabled')}</div>
                  <ToggleSwitch checked={isTrue(settings.survey_enabled)} onChange={() => updateSetting('survey_enabled', isTrue(settings.survey_enabled) ? 'false' : 'true')} />
                </div>
                <div className="flex items-center justify-between gap-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-content">{t('survey.config.autoSend')}</div>
                    <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('survey.config.autoSendDesc')}</div>
                  </div>
                  <ToggleSwitch checked={isTrue(settings.survey_auto_send)} onChange={() => updateSetting('survey_auto_send', isTrue(settings.survey_auto_send) ? 'false' : 'true')} />
                </div>
                <div className="flex items-center justify-between gap-6 py-4">
                  <div className="text-sm font-medium text-content">{t('survey.config.reminderEnabled')}</div>
                  <ToggleSwitch checked={isTrue(settings.survey_reminder_enabled)} onChange={() => updateSetting('survey_reminder_enabled', isTrue(settings.survey_reminder_enabled) ? 'false' : 'true')} />
                </div>
                <div className="flex items-center justify-between gap-6 py-4">
                  <div className="text-sm font-medium text-content">{t('survey.config.reminderDays')}</div>
                  <input type="number" min="1" max="30" value={settings.survey_reminder_days || '5'}
                    onChange={e => updateSetting('survey_reminder_days', e.target.value)}
                    className={`w-24 ${inp}`} />
                </div>
                <div className="flex items-center justify-between gap-6 py-4">
                  <div className="text-sm font-medium text-content">{t('survey.config.closeDays')}</div>
                  <input type="number" min="1" max="60" value={settings.survey_close_days || '14'}
                    onChange={e => updateSetting('survey_close_days', e.target.value)}
                    className={`w-24 ${inp}`} />
                </div>
                <div className="flex items-center justify-between gap-6 py-4">
                  <div className="text-sm font-medium text-content">{t('survey.config.minSegment')}</div>
                  <select value={settings.survey_min_segment || 'C'} onChange={e => updateSetting('survey_min_segment', e.target.value)} className={`w-24 ${inp}`}>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                  </select>
                </div>
                <div className="flex items-center justify-between gap-6 py-4">
                  <div className="text-sm font-medium text-content">{t('survey.config.excludeInactive')}</div>
                  <ToggleSwitch checked={isTrue(settings.survey_exclude_inactive)} onChange={() => updateSetting('survey_exclude_inactive', isTrue(settings.survey_exclude_inactive) ? 'false' : 'true')} />
                </div>
                <div className="flex items-center justify-between gap-6 py-4">
                  <div className="text-sm font-medium text-content">{t('survey.eskalation.schwelle')}</div>
                  <input type="number" min="1" max="5" value={settings.survey_eskalation_schwelle || '2'}
                    onChange={e => updateSetting('survey_eskalation_schwelle', e.target.value)}
                    className={`w-24 ${inp}`} />
                </div>
                <div className="flex items-center justify-between gap-6 py-4">
                  <div className="text-sm font-medium text-content">{t('survey.eskalation.nps')}</div>
                  <input type="number" min="0" max="10" value={settings.survey_eskalation_nps || '4'}
                    onChange={e => updateSetting('survey_eskalation_nps', e.target.value)}
                    className={`w-24 ${inp}`} />
                </div>
                <div className="py-4 flex justify-end">
                  <button onClick={saveSettings} disabled={settingsSaving}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
                    <Save className="w-3.5 h-3.5" /> {settingsSaving ? '...' : t('common.save')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      <NewSurveyModal open={showNewModal} onClose={() => setShowNewModal(false)} onCreated={() => { showToast(t('survey.admin.createSuccess')); loadSurveys(); loadStats(); }} t={t} />
      <ConfirmModal open={!!confirm} message={confirm?.message} loading={confirm?.loading}
        onConfirm={() => confirm?.action?.()} onCancel={() => setConfirm(null)} />

      {/* Edit Frage Modal */}
      {editFrage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditFrage(null)} />
          <div className="relative bg-surface-elevated border border-line rounded-card shadow-elevated w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-content">{editFrage.id ? t('survey.fragen.edit') : t('survey.fragen.add')}</h3>
              <button onClick={() => setEditFrage(null)} className="p-1 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)]"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('survey.fragen.labelDe')}</span>
                  <input value={editFrage.label_de || ''} onChange={e => setEditFrage(p => ({ ...p, label_de: e.target.value }))} className={`mt-1 ${inp}`} />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('survey.fragen.labelEn')}</span>
                  <input value={editFrage.label_en || ''} onChange={e => setEditFrage(p => ({ ...p, label_en: e.target.value }))} className={`mt-1 ${inp}`} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('survey.fragen.descDe')}</span>
                  <textarea value={editFrage.beschreibung_de || ''} onChange={e => setEditFrage(p => ({ ...p, beschreibung_de: e.target.value }))} rows={2} className={`mt-1 ${inp} resize-none`} />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('survey.fragen.descEn')}</span>
                  <textarea value={editFrage.beschreibung_en || ''} onChange={e => setEditFrage(p => ({ ...p, beschreibung_en: e.target.value }))} rows={2} className={`mt-1 ${inp} resize-none`} />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">{t('survey.fragen.type')}</span>
                  <select value={editFrage.typ || 'stars'} onChange={e => setEditFrage(p => ({ ...p, typ: e.target.value }))} className={`mt-1 ${inp}`}>
                    <option value="stars">{t('survey.fragen.types.stars')}</option>
                    <option value="nps">{t('survey.fragen.types.nps')}</option>
                    <option value="text">{t('survey.fragen.types.text')}</option>
                    <option value="yesno">{t('survey.fragen.types.yesno')}</option>
                  </select>
                </label>
                <label className="flex items-center gap-3 mt-5">
                  <ToggleSwitch checked={editFrage.pflicht !== false} onChange={() => setEditFrage(p => ({ ...p, pflicht: !p.pflicht }))} size="sm" />
                  <span className="text-sm text-content">{t('survey.fragen.required')}</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditFrage(null)} className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">{t('common.cancel')}</button>
              <button onClick={() => saveFrage(editFrage)} disabled={frageSaving || !editFrage.label_de}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
                {frageSaving ? '...' : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empfänger-Modal */}
      {empfaengerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !empfaengerModal.sending && setEmpfaengerModal(null)} />
          <div className="relative bg-surface-elevated border border-line rounded-card shadow-elevated w-full max-w-xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-line">
              <h3 className="text-sm font-bold text-content">{t('survey.empfaenger.title')}</h3>
              <button onClick={() => !empfaengerModal.sending && setEmpfaengerModal(null)} className="p-1 rounded-lg hover:bg-surface-hover text-[var(--text-secondary)]"><X className="w-4 h-4" /></button>
            </div>

            {empfaengerModal.loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
            ) : (
              <>
                {/* Select all + search */}
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-line">
                  <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
                    <input type="checkbox"
                      checked={empfaengerModal.empfaenger.filter(e => e.email).every(e => e.selected)}
                      onChange={e => toggleAllEmpfaenger(e.target.checked)}
                      className="rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc]" />
                    {t('survey.empfaenger.selectAll')} ({empfaengerModal.empfaenger.filter(e => e.email && e.selected).length})
                  </label>
                  <input
                    type="text"
                    placeholder={t('survey.empfaenger.search')}
                    value={empfaengerModal.search || ''}
                    onChange={e => setEmpfaengerModal(prev => ({ ...prev, search: e.target.value }))}
                    className={`w-48 ${inp}`}
                  />
                </div>

                {/* List */}
                <div className="max-h-80 overflow-y-auto divide-y divide-line">
                  {empfaengerModal.empfaenger
                    .filter(e => !empfaengerModal.search || e.name.toLowerCase().includes(empfaengerModal.search.toLowerCase()))
                    .map(e => (
                      <label key={e.kunde_id}
                        className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                          e.email ? 'cursor-pointer hover:bg-surface-hover' : 'opacity-50 cursor-not-allowed'
                        }`}>
                        <input type="checkbox"
                          checked={!!e.selected}
                          disabled={!e.email}
                          onChange={() => toggleEmpfaenger(e.kunde_id)}
                          className="rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc] disabled:opacity-40" />
                        <span className="flex-1 text-content truncate">{e.name}</span>
                        {e.segment && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            e.segment === 'A' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : e.segment === 'B' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-gray-100 text-[var(--text-secondary,#4B5563)] dark:bg-gray-800 dark:text-[var(--text-tertiary,#6B7280)]'
                          }`}>{e.segment}</span>
                        )}
                        <span className="text-xs text-[var(--text-secondary)] truncate max-w-[160px]">
                          {e.email || t('survey.empfaenger.noEmail')}
                        </span>
                      </label>
                    ))}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-line">
                  {empfaengerModal.empfaenger.filter(e => !e.email).length > 0 && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mb-2">
                      {t('survey.empfaenger.noEmailHint', { count: empfaengerModal.empfaenger.filter(e => !e.email).length })}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[var(--text-secondary)]">
                      {t('survey.empfaenger.selected', {
                        count: empfaengerModal.empfaenger.filter(e => e.selected).length,
                        total: empfaengerModal.empfaenger.length,
                      })}
                    </span>
                    <div className="flex gap-2">
                      <button onClick={() => setEmpfaengerModal(null)} disabled={empfaengerModal.sending}
                        className="px-4 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">
                        {t('common.cancel')}
                      </button>
                      <button onClick={sendToSelected}
                        disabled={empfaengerModal.sending || empfaengerModal.empfaenger.filter(e => e.selected).length === 0}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50">
                        <Send className="w-3.5 h-3.5" />
                        {empfaengerModal.sending ? '...' : t('survey.empfaenger.sendButton', { count: empfaengerModal.empfaenger.filter(e => e.selected).length })}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteFrage}
        onCancel={() => setConfirmDeleteFrage(null)}
        onConfirm={() => { const id = confirmDeleteFrage; setConfirmDeleteFrage(null); deleteFrage(id); }}
        title={t('common.delete', 'Delete')}
        message={t('survey.fragen.confirmDelete')}
        confirmLabel={t('common.delete', 'Delete')}
        cancelLabel={t('common.cancel', 'Cancel')}
        variant="danger"
      />

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
